import React, { useState } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { createItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert } from "../utils/messages";
import { ReceivedDocument } from "../../models/received-document";
import QrScannerModal from "./QrScannerModal";

interface ReceivedDocumentCdcEntryProps {
  onSuccess: (data: ReceivedDocument, cdc: string) => void;
  onManual: () => void;
}

const ReceivedDocumentCdcEntry: React.FC<ReceivedDocumentCdcEntryProps> = ({
  onSuccess,
  onManual,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [cdc, setCdc] = useState("");
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const onCdcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCdc(e.target.value.trim());
  };

  const performLookup = async (cdcValue: string) => {
    if (!cdcValue || cdcValue.length !== 44) {
      errorAlert("El CDC debe tener exactamente 44 caracteres");
      return;
    }

    const docTypeCode = cdcValue.substring(2, 4);
    if (docTypeCode === "04") {
      errorAlert(
        "Este CDC corresponde a una Autofactura, que no está soportada como documento recibido.",
      );
      return;
    }
    if (docTypeCode === "07") {
      errorAlert(
        "Este CDC corresponde a una Nota de Remisión, que no está soportada como documento recibido.",
      );
      return;
    }

    setLoading(true);

    const status = await createItem<ReceivedDocument>(
      "/received_documents/lookup-by-cdc/",
      { cdc: cdcValue, taxPayerId: taxPayer.id },
    );

    setLoading(false);

    if (status.status !== SUCCESS) {
      let message = "Error al consultar SIFEN";
      if (status.detail !== undefined) {
        message = status.detail;
      }
      errorAlert(message);
      return;
    }

    if (status.data) {
      onSuccess(status.data, cdcValue);
    }
  };

  const onConsultClick = () => {
    performLookup(cdc);
  };

  const onQrScan = (text: string) => {
    setShowScanner(false);

    // Parse QR URL to extract CDC and optionally validate receiver RUC
    try {
      const url = new URL(text);
      const cdcFromQr = url.searchParams.get("Id");
      const rucRecFromQr = url.searchParams.get("dRucRec");

      if (!cdcFromQr || cdcFromQr.length !== 44) {
        errorAlert(
          "El código QR escaneado no contiene un CDC válido. Ingresá el CDC manualmente.",
        );
        return;
      }

      // If the QR has dRucRec, validate it matches the active taxPayer before querying SIFEN
      if (rucRecFromQr) {
        const taxPayerBaseRuc = (taxPayer.ruc ?? "").split("-")[0];
        if (rucRecFromQr !== taxPayerBaseRuc) {
          errorAlert(
            `El documento está dirigido al RUC ${rucRecFromQr}, no al contribuyente activo (${taxPayerBaseRuc})`,
          );
          return;
        }
      }

      setCdc(cdcFromQr);
      performLookup(cdcFromQr);
    } catch {
      errorAlert(
        "El código QR escaneado no es un URL válido de SIFEN. Ingresá el CDC manualmente.",
      );
    }
  };

  return (
    <>
      <CCard className="shadow-sm border-0">
        <CCardHeader
          className="d-flex align-items-center"
          style={{
            background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
            color: "white",
            borderBottom: "none",
            padding: "14px 20px",
          }}
        >
          <i className="fa fa-file-text-o mr-2" style={{ fontSize: "1.1rem" }} />
          <span style={{ fontWeight: 600, fontSize: "1rem" }}>
            Agregar Documento Recibido
          </span>
        </CCardHeader>
        <CCardBody>
          <CFormGroup>
            <CRow className="justify-content-center">
              <CCol md={8}>
                <div className="text-center mb-4">
                  <CButton
                    color="info"
                    onClick={() => setShowScanner(true)}
                    disabled={loading}
                    style={{ minWidth: "180px" }}
                  >
                    <i className="fa fa-camera mr-2" />
                    Escanear QR
                  </CButton>
                </div>

                <CLabel>
                  <strong>CDC</strong>{" "}
                  <span className="text-muted">(44 caracteres)</span>
                </CLabel>
                <CRow>
                  <CCol>
                    <CInput
                      type="text"
                      value={cdc}
                      onChange={onCdcChange}
                      placeholder="01800169514002010020789112026040215736922636"
                      maxLength={44}
                      disabled={loading}
                    />
                    <small className="text-muted">
                      {cdc.length} / 44 caracteres
                    </small>
                  </CCol>
                  <CCol md="auto">
                    <CButton
                      color="primary"
                      onClick={onConsultClick}
                      disabled={loading || cdc.length !== 44}
                      style={{ minWidth: "130px" }}
                    >
                      {loading ? (
                        <Spinner
                          animation="grow"
                          style={{
                            height: "17px",
                            width: "17px",
                            marginRight: "8px",
                          }}
                        />
                      ) : null}
                      {loading ? "Consultando..." : "Consultar"}
                    </CButton>
                  </CCol>
                </CRow>

                <hr className="my-4" />

                <div className="text-center">
                  <span className="text-muted">— o —</span>
                  <div className="mt-3">
                    <CButton
                      color="link"
                      onClick={onManual}
                      disabled={loading}
                    >
                      Cargar manualmente
                    </CButton>
                  </div>
                </div>
              </CCol>
            </CRow>
          </CFormGroup>
        </CCardBody>
      </CCard>

      <QrScannerModal
        show={showScanner}
        onScan={onQrScan}
        onClose={() => setShowScanner(false)}
      />
    </>
  );
};

export default ReceivedDocumentCdcEntry;
